import React from 'react';
import { Link } from '../ui/link/Link';
import styles from './skip-link.scss';

type BaseLinkProps = {
  url?: string;
  external?: boolean;
  children?: React.ReactNode;
  itemClassName?: string;
  anchorClassName?: string;
  handler?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  keyboardHandler?: (e: React.KeyboardEvent<HTMLAnchorElement>) => void;
};

const TypedLink = Link as React.ComponentType<BaseLinkProps>;

type SkipLinkProps = Omit<
  BaseLinkProps,
  'itemClassName' | 'anchorClassName' | 'keyboardHandler'
> & {
  keyboardHandler?: (e: React.KeyboardEvent<HTMLAnchorElement>) => void;
};

export const SkipLink = (props: SkipLinkProps) => {
  const { keyboardHandler, ...linkProps } = props;

  const handleKeyboard = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    keyboardHandler?.(e);

    const { nativeEvent } = e;

    const { href } = nativeEvent.target as HTMLAnchorElement;
    const keycode = nativeEvent.keyCode;

    if (keycode === 13 || keycode === 32) {
      // Enter or Space key
      nativeEvent.preventDefault();
      const hash = href.split('#')[1];
      const targetEl = document.querySelector(`#${hash}`);

      if (targetEl) {
        // Ensure the target element is focusable
        if (!targetEl.hasAttribute('tabindex')) {
          targetEl.setAttribute('tabindex', '-1');
        }

        // Webkit requires a timeout in order to shift focus
        window.setTimeout(() => {
          (targetEl as HTMLElement).scrollIntoView(true);
          (targetEl as HTMLElement).focus();
        }, 0);
      }
    }
  };

  return (
    <TypedLink
      {...linkProps}
      itemClassName={styles.skipLinkItem}
      anchorClassName={styles.skipLink}
      keyboardHandler={handleKeyboard}
    />
  );
};
